'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { ComponentPropsWithoutRef } from 'react';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import './product-detail.scss';

type Props = {
    layout?: 'grid' | 'row';
    version?: 'default' | 'box';
    isRequired?: boolean;
    done?: boolean;
    nested?: boolean;
    collapsible?: boolean;
    picked?: string;
    selectedCount?: number;
    closed?: boolean;
    onClosedChange?: (closed: boolean) => void;
    count: number;
    name: string;
    description?: string;
    minSelection?: number;
    maxSelection?: number;
} & ComponentPropsWithoutRef<'div'>;

export const ProductDetailAddonsAddonSection = ({
    layout = 'row',
    version = 'default',
    children,
    isRequired,
    done = false,
    nested = false,
    collapsible = false,
    picked,
    selectedCount,
    closed = false,
    onClosedChange,
    name,
    description,
    minSelection,
    maxSelection,
    count,
    ...props
}: Props) => {
    const t = useTranslations();
    const seen = useRef(false);
    const pickedRef = useRef(picked);
    const userChanged = useRef(false);
    const onClosedChangeRef = useRef(onClosedChange);
    useEffect(() => {
        onClosedChangeRef.current = onClosedChange;
    }, [onClosedChange]);
    useEffect(() => {
        if (!seen.current) {
            seen.current = true;
            pickedRef.current = picked;
            return;
        }
        if (picked !== pickedRef.current) {
            pickedRef.current = picked;
            userChanged.current = true;
        }
        if (!userChanged.current || !collapsible) return;
        userChanged.current = false;
        onClosedChangeRef.current?.(true);
    }, [picked, collapsible]);
    const opened = !closed;
    const collapsed = collapsible && closed;
    const overMax = maxSelection != null && selectedCount != null && selectedCount > maxSelection;
    const header = (
        <>
            <div className="product-detail__addons-addon-section-title-wrapper">
                <div className="product-detail__addons-addon-section-title-line">
                    <h3
                        className="product-detail__addons-addon-section-title"
                        data-testid="product-detail-addon-section-name"
                    >
                        {name}
                    </h3>
                    <Badge
                        shape="rounded"
                        className="product-detail__addons-addon-section-count-badge"
                        data-testid="product-detail-addon-section-count"
                    >
                        {count}
                    </Badge>
                </div>
                {collapsed && picked && (
                    <p
                        className="product-detail__addons-addon-section-picked"
                        data-testid="product-detail-addon-section-picked"
                    >
                        {picked}
                    </p>
                )}
            </div>
            <div className="product-detail__addons-addon-section-badges">
                <Badge
                    className={cn(
                        'product-detail__addons-addon-section-status-badge',
                        done
                            ? 'product-detail__addons-addon-section-status-badge--done'
                            : isRequired
                              ? 'product-detail__addons-addon-section-status-badge--required'
                              : 'product-detail__addons-addon-section-status-badge--optional',
                    )}
                    data-testid="product-detail-addon-section-status"
                    data-required={isRequired ? 'true' : 'false'}
                    data-done={done ? 'true' : 'false'}
                >
                    {done
                        ? t('PRODUCT_DETAIL.SELECTED')
                        : isRequired
                          ? t('PRODUCT_DETAIL.REQUIRED')
                          : t('PRODUCT_DETAIL.OPTIONAL')}
                </Badge>
                {!collapsed && maxSelection != null && selectedCount != null && (
                    <Badge
                        className={cn(
                            'product-detail__addons-addon-section-qty-badge',
                            overMax && 'product-detail__addons-addon-section-qty-badge--over',
                        )}
                        data-testid="product-detail-addon-section-selected-count"
                    >
                        {selectedCount}/{maxSelection}
                    </Badge>
                )}
                {collapsible && (
                    <span
                        className={cn(
                            'product-detail__addons-addon-section-chevron',
                            !collapsed && 'product-detail__addons-addon-section-chevron--open',
                        )}
                        aria-hidden="true"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                        >
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </span>
                )}
            </div>
        </>
    );
    return (
        <div
            {...props}
            data-testid="product-detail-addon-section"
            data-done={done ? 'true' : 'false'}
            data-nested={nested ? 'true' : 'false'}
            data-collapsed={collapsed ? 'true' : 'false'}
            data-over={overMax ? 'true' : 'false'}
            onClickCapture={(event) => {
                if (!collapsible || !opened) return;
                const target = event.target as HTMLElement;
                if (target.closest('[data-testid="product-detail-addon-section-toggle"]')) return;
                if (target.closest('[data-testid="product-detail__addons-amount-item-input-wrapper"]'))
                    return;
                // A click inside a nested card belongs to that card, not this section.
                if (!nested && target.closest('[data-nested="true"]')) return;
                const block = target.closest('[data-member-block]');
                if (!block || block.getAttribute('data-opens-more') === 'true') return;
                // A new pick collapses through the `picked` effect once it is stored. Collapsing
                // here would unmount the radio before the click reaches it and drop the pick.
                if (!block.querySelector('[data-selected="true"]')) return;
                // Keep the current pick: the radio would otherwise clear it.
                event.preventDefault();
                event.stopPropagation();
                onClosedChange?.(true);
            }}
            className={cn(
                'product-detail__addons-addon-section',
                version === 'box' && 'product-detail__addons-addon-section--box',
                version === 'box' && isRequired && 'product-detail__addons-addon-section--box-required',
                version === 'box' && done && 'product-detail__addons-addon-section--done',
                nested && 'product-detail__addons-addon-section--nested',
                version === 'box' && overMax && 'product-detail__addons-addon-section--over',
                props.className,
            )}
        >
            {collapsible ? (
                <button
                    type="button"
                    className="product-detail__addons-addon-section-header product-detail__addons-addon-section-header--toggle"
                    data-testid="product-detail-addon-section-toggle"
                    aria-expanded={!collapsed}
                    onClick={() => onClosedChange?.(!closed)}
                >
                    {header}
                </button>
            ) : (
                <div className="product-detail__addons-addon-section-header">{header}</div>
            )}
            {!collapsed && description && (
                <p
                    className="product-detail__addons-addon-section-description"
                    data-testid="product-detail-addon-section-description"
                >
                    {description}
                </p>
            )}
            {!collapsed && (minSelection || maxSelection) && (
                <p className="product-detail__addons-addon-section-minmax">
                    {!!minSelection && (
                        <span
                            data-testid="product-detail-addon-section-min-selection"
                            data-value={minSelection}
                        >
                            {t('PRODUCT_DETAIL.MIN', { count: minSelection })}
                        </span>
                    )}
                    {!!minSelection && !!maxSelection && ' · '}
                    {!!maxSelection && (
                        <span
                            data-testid="product-detail-addon-section-max-selection"
                            data-value={maxSelection}
                        >
                            {t('PRODUCT_DETAIL.MAX', { count: maxSelection })}
                        </span>
                    )}
                </p>
            )}
            {!collapsed && (
                <div
                    className={cn(
                        'product-detail__addons-addon-section-children',
                        layout === 'row' && 'product-detail__addons-addon-section-children--row',
                        layout === 'grid' && 'product-detail__addons-addon-section-children--grid',
                    )}
                >
                    {children}
                </div>
            )}
        </div>
    );
};
